<template>
  <o-modal
    ref="modal"
    :title="$t('components.customer.actions.changeDateOfBirth.title')"
    :info="$t('components.customer.actions.changeDateOfBirth.info')"
    :loading="loading"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-md relative"
    content-class="mx-auto max-w-lg h-full flex flex-col"
  >
    <div class="-mt-5 text-left h-full flex flex-col justify-between">
      <o-scroll v-if="customer" class="h-full pt-5">
        <div v-if="!customer.isNewRecord" class="flex flex-col items-centerjustify-center mb-8">
          <o-avatar :items="[customer]" size="xl" class="mx-auto block" />
          <h2 class="inline mx-auto font-semibold text-gray-800 text-lg mt-2 dark:text-gray-100">
            {{ customer.displayName }}
          </h2>
        </div>

        <o-form ref="form">
          <div class="mx-2 mb-3">
            <o-field-group
              class="w-full md:w-1/2"
              :label="$t('general.dateOfBirth')"
              date
              :message="ageLabel"
            >
              <date-field
                v-model="newDateOfBirth"
                :augmenter="Augmenters.YMD"
                visibility="focus"
              />
            </o-field-group>
          </div>

          <div class="bg-gray-50 border rounded-md p-3 mb-6 mx-2 dark:bg-gray-800/30">
            <div class="flex items-center justify-between">
              <div>
                <h5 class="text-gray-700 font-medium dark:text-gray-200">
                  {{ $t('customer.verification.verified') }}
                </h5>
                <p class="text-sm text-gray-600 dark:text-gray-300">
                  {{ $t('components.customer.actions.changeDateOfBirth.verifiedInfo') }}
                </p>
              </div>

              <toggle-field v-model="ageVerified" :status="status" />
            </div>

            <div v-if="ageVerified" class="border-t pt-3 mt-3">
              <div class="flex flex-col space-y-4">
                <div v-if="ageVerified">
                  <o-field-group class="w-full" :label="$t('components.customer.actions.changeDateOfBirth.verifiedVia')" :info="$t('components.customer.actions.changeDateOfBirth.verifiedViaInfo')" rules="required">
                    <select-field v-model="verifiedVia" :options="verifiedViaOptions" />
                  </o-field-group>
                </div>
                <div v-if="ageVerified && verifiedViaOther">
                  <o-field-group class="w-full" :label="$t('general.other')" :info="$t('components.customer.actions.changeDateOfBirth.verifiedOtherInfo')" rules="required">
                    <text-field v-model="verifiedOther" />
                  </o-field-group>
                </div>
              </div>
            </div>
          </div>
        </o-form>
      </o-scroll>

      <div class="mx-2 mt-4">
        <o-button :disabled="!newDateOfBirth" class="w-full" variant="success" @click="handleConfirm">
          {{ $t('general.confirm') }}
        </o-button>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  OAvatar,
  OFieldGroup,
} from '@/components/elements'

import {
  DateField,
  ToggleField,
  SelectField,
  TextField,
} from '@/components/fields'

import { CustomerVerifiedVia, CustomerVerifiedViaLabels } from '~/_api/models/customer'

interface Emits {
  (e: 'update', customer: any): void
}

const emit = defineEmits<Emits>()
const { $api, $date, $dateFns, $i18n, $notification } = useNuxtApp()

const modal = ref<any>()
const form = ref<any>()
const loading = ref(false)
const customer = ref<any>(null)
const ageVerified = ref(false)
const newDateOfBirth = ref<any>(null)
const verifiedVia = ref<CustomerVerifiedVia | null>(null)
const verifiedOther = ref<string | null>(null)

const verifiedViaOptions = computed(() => {
  return Object.values(CustomerVerifiedVia)
    .filter((item): item is CustomerVerifiedVia => typeof item === 'number')
    .map((option) => {
      return {
        label: $i18n.t(CustomerVerifiedViaLabels[option]),
        value: option,
      }
    })
})

const verifiedViaOther = computed(() => verifiedVia.value === CustomerVerifiedVia.other)

const ageLabel = computed(() => {
  if (!newDateOfBirth.value) {
    return ''
  }

  const date = $date.parse(newDateOfBirth.value.toString(), 'yyyyMMdd')
  const age = $dateFns.differenceInYears(new Date(), date)

  return $i18n.t('components.customer.actions.changeDateOfBirth.ageLabel', { age })
})

const open = (cust: any) => {
  customer.value = cust
  ageVerified.value = cust.ageVerified
  newDateOfBirth.value = cust.dateOfBirth?.value ?? null
  verifiedVia.value = cust.ageVerifiedDetail?.verifiedVia ?? null

  modal.value.open()
}

const handleConfirm = async () => {
  loading.value = true
  const valid = await form.value.validate()
  if (!valid) {
    loading.value = false
    return
  }

  try {
    const ageVerifiedDetail = {
      dateOfBirth: newDateOfBirth.value,
      ageVerified: ageVerified.value,
      verifiedVia: verifiedVia.value ?? CustomerVerifiedVia.other,
      verifiedOther: verifiedOther.value,
    }

    const response = await $api.customer.verifyAge(
      customer.value.idCustomer.toString(),
      ageVerifiedDetail,
    )

    if (response.data) {
      $notification.toast({
        title: $i18n.t('components.customer.actions.changeDateOfBirth.success'),
        text: $i18n.t('components.customer.actions.changeDateOfBirth.successInfo', { name: customer.value.displayName }),
        variant: 'success',
      })

      emit('update', response.data)
      modal.value.close()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e.errors })
  }

  loading.value = false
}

defineExpose({ open })
</script>
